import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAdminAnalytics, AdminAnalyticsData } from '../api/admin.api';

const AUTO_REFRESH_MS = 60_000;
const STORAGE_KEY = 'admin_dashboard_period';
const CACHE_MAX_AGE_MS = 90_000; // 1.5 min

const analyticsCache = new Map<string, { data: AdminAnalyticsData; at: number }>();

function getCacheKey(from: string, to: string): string {
  return `${from || 'none'}-${to || 'none'}`;
}

export type PeriodPreset = 'today' | '7d' | '30d' | 'all' | 'custom';

function getPresetRange(preset: PeriodPreset): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  if (preset === 'today') {
    return { from: to, to };
  }
  if (preset === 'all') {
    return { from: '2000-01-01', to };
  }
  const from = new Date(now);
  if (preset === '7d') {
    from.setDate(from.getDate() - 6);
  } else if (preset === '30d') {
    from.setDate(from.getDate() - 29);
  } else {
    // custom: первый день текущего месяца
    from.setDate(1);
    return { from: from.toISOString().slice(0, 10), to };
  }
  return { from: from.toISOString().slice(0, 10), to };
}

/** Первый день текущего месяца в YYYY-MM-DD */
function getFirstDayOfCurrentMonth(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function parseStoredPeriod(): { preset: PeriodPreset; from: string; to: string } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { preset?: string; from?: string; to?: string };
    if (v.preset && ['today', '7d', '30d', 'all', 'custom'].includes(v.preset)) {
      return {
        preset: v.preset as PeriodPreset,
        from: typeof v.from === 'string' ? v.from : '',
        to: typeof v.to === 'string' ? v.to : '',
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function useAdminAnalytics(initialPreset: PeriodPreset = '30d') {
  const [searchParams, setSearchParams] = useSearchParams();
  // CHANGE: persist period in URL + sessionStorage
  const urlPreset = (searchParams.get('preset') as PeriodPreset) || null;
  const urlFrom = searchParams.get('from') ?? '';
  const urlTo = searchParams.get('to') ?? '';
  const stored = parseStoredPeriod();
  const initialPresetValue: PeriodPreset =
    urlPreset && ['today', '7d', '30d', 'all', 'custom'].includes(urlPreset)
      ? urlPreset
      : stored?.preset ?? initialPreset;
  const firstDayMonth = getFirstDayOfCurrentMonth();
  const todayYmd = new Date().toISOString().slice(0, 10);
  const [preset, setPresetState] = useState<PeriodPreset>(() => initialPresetValue);
  const [customFrom, setCustomFrom] = useState(() => {
    const v = (urlFrom || stored?.from) ?? '';
    if (initialPresetValue === 'custom' && (!v || v === '2000-01-01')) return firstDayMonth;
    return v;
  });
  const [customTo, setCustomTo] = useState(() => {
    const v = (urlTo || stored?.to) ?? '';
    if (initialPresetValue === 'custom' && !v) return todayYmd;
    return v;
  });
  const [data, setData] = useState<AdminAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const from = preset === 'custom' ? customFrom : getPresetRange(preset).from;
  const to = preset === 'custom' ? customTo : getPresetRange(preset).to;

  const setPreset = useCallback(
    (p: PeriodPreset) => {
      setPresetState(p);
      const range =
        p === 'custom'
          ? customFrom && customTo
            ? { from: customFrom, to: customTo }
            : getPresetRange('custom')
          : getPresetRange(p);
      if (p === 'custom' && (!customFrom || !customTo)) {
        setCustomFrom(range.from);
        setCustomTo(range.to);
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('preset', p);
        next.set('from', range.from);
        next.set('to', range.to);
        return next;
      });
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ preset: p, from: range.from, to: range.to }));
    },
    [customFrom, customTo, setSearchParams]
  );
  const setCustomFromPersist = useCallback(
    (value: string) => {
      setCustomFrom(value);
      if (preset === 'custom') {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('from', value);
          return next;
        });
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ preset, from: value, to }));
      }
    },
    [preset, to, setSearchParams]
  );
  const setCustomToPersist = useCallback(
    (value: string) => {
      setCustomTo(value);
      if (preset === 'custom') {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('to', value);
          return next;
        });
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ preset, from, to: value }));
      }
    },
    [preset, from, setSearchParams]
  );

  const fetchAnalytics = useCallback(() => {
    const isAll = preset === 'all';
    const fromParam = isAll ? undefined : (from && to ? from : undefined);
    const toParam = isAll ? undefined : (from && to ? to : undefined);
    const cacheKey = isAll ? 'all' : getCacheKey(fromParam ?? '', toParam ?? '');
    const cached = analyticsCache.get(cacheKey);
    if (cached && Date.now() - cached.at < CACHE_MAX_AGE_MS) {
      setData(cached.data);
      setLoading(false);
      setError(null);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    const params = isAll ? { period: 'all' as const } : { from: fromParam, to: toParam };
    getAdminAnalytics(params)
      .then((payload) => {
        if (ac.signal.aborted) return;
        analyticsCache.set(cacheKey, { data: payload, at: Date.now() });
        setData(payload);
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
        setData(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
  }, [preset, from, to]);

  useEffect(() => {
    fetchAnalytics();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchAnalytics]);

  // CHANGE: pause auto-refresh when tab hidden to avoid extra requests (audit)
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchAnalytics();
    };
    intervalRef.current = setInterval(tick, AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchAnalytics]);

  const setPeriod = useCallback(
    (p: PeriodPreset, range?: { from: string; to: string }) => {
      if (p === 'custom' && range) {
        setCustomFrom(range.from);
        setCustomTo(range.to);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('preset', 'custom');
          next.set('from', range.from);
          next.set('to', range.to);
          return next;
        });
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ preset: 'custom', from: range.from, to: range.to }));
        setPresetState('custom');
      } else {
        setPreset(p);
      }
    },
    [setPreset, setSearchParams]
  );

  return {
    data,
    loading,
    error,
    preset,
    from,
    to,
    setPreset,
    setCustomFrom: setCustomFromPersist,
    setCustomTo: setCustomToPersist,
    setPeriod,
    refetch: fetchAnalytics,
  };
}
